import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import timeOptions from "../../../../constants/timeOptions";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/*
 * Car availability for extending a reservation. A port of the booking frontend's
 * `src/lib/carCalendarBlocks.ts`, so staff and customers are offered exactly the same slots.
 *
 * Every helper takes the pickup branch's `timeZone`: a calendar day + "HH:mm" slot is read on that
 * clock, so slots line up with the stored UTC instants no matter where the admin is sitting.
 * Ranges are `{ start: Dayjs, end: Dayjs }`.
 */

const slotAt = (date, time, timeZone) => {
    const wallClock = `${date.format("YYYY-MM-DD")} ${time}`;
    return timeZone ? dayjs.tz(wallClock, "YYYY-MM-DD HH:mm", timeZone) : dayjs(wallClock, "YYYY-MM-DD HH:mm");
};

/** The calendar day an instant falls on, on the given clock, as a plain (browser-local) day. */
export const calendarDayOf = (instant, timeZone) =>
    timeZone ? dayjs(instant.tz(timeZone).format("YYYY-MM-DD"), "YYYY-MM-DD") : instant.startOf("day");

const toCalendarRange = (startValue, endValue) => {
    if (!startValue || !endValue) return null;
    const start = dayjs(startValue);
    const end = dayjs(endValue);
    if (!start.isValid() || !end.isValid()) return null;
    return { start, end };
};

const isDayFullyCoveredByRanges = (date, ranges, timeZone) => {
    if (!ranges.length) return false;

    const day = date.startOf("day");
    return timeOptions.every(({ value: time }) => {
        const slot = slotAt(day, time, timeZone);
        if (!slot.isValid()) return true;
        return ranges.some((range) => !slot.isBefore(range.start) && !slot.isAfter(range.end));
    });
};

const isDayInsideCarBlock = (date, carBlockRanges, timeZone) => {
    if (!carBlockRanges.length) return false;

    const day = date.startOf("day");
    return carBlockRanges.some((range) => {
        const blockStart = calendarDayOf(range.start, timeZone);
        const blockEnd = calendarDayOf(range.end, timeZone);
        return !day.isBefore(blockStart, "day") && !day.isAfter(blockEnd, "day");
    });
};

export const isDayBlockedByCalendar = (date, reservationRanges, carBlockRanges, timeZone) =>
    isDayInsideCarBlock(date, carBlockRanges, timeZone) || isDayFullyCoveredByRanges(date, reservationRanges, timeZone);

/** `startDate` is an instant (the current drop-off); `endDate` is a picked calendar day. */
export const rangeCrossesBlockedDay = (startDate, endDate, reservationRanges, carBlockRanges, timeZone) => {
    let cursor = calendarDayOf(startDate, timeZone).add(1, "day");
    const lastDay = endDate.startOf("day");

    while (!cursor.isAfter(lastDay, "day")) {
        if (isDayBlockedByCalendar(cursor, reservationRanges, carBlockRanges, timeZone)) return true;
        cursor = cursor.add(1, "day");
    }
    return false;
};

/** Extension window [extensionStart, newDropoff] overlaps a blocked range (inclusive). */
const extensionIntervalOverlapsRange = (extensionStart, newDropoff, range) =>
    !extensionStart.isAfter(range.end) && !newDropoff.isBefore(range.start);

/**
 * The drop-off a by-day extension lands on: whole 24-hour periods after the current drop-off, so
 * the rental clock keeps running from the same instant and billing adds exactly `days` days.
 */
export const addExtensionDays = (extensionStart, days) => extensionStart.add(days * 24, "hour");

/** True when the window [extensionStart, newDropoff] runs into any blocked range. */
export const isExtensionRangeBlocked = (extensionStart, newDropoff, ranges) =>
    ranges.some((range) => extensionIntervalOverlapsRange(extensionStart, newDropoff, range));

export const isExtensionTimeBlocked = (date, time, ranges, extensionStart, timeZone) => {
    if (!date?.isValid() || !time || !extensionStart?.isValid()) return true;

    const newDropoff = slotAt(date, time, timeZone);
    if (!newDropoff.isValid() || !newDropoff.isAfter(extensionStart)) return true;

    return isExtensionRangeBlocked(extensionStart, newDropoff, ranges);
};

export const isExtensionDayFullyBlocked = (date, ranges, extensionStart, timeZone) =>
    timeOptions.every(({ value: time }) => isExtensionTimeBlocked(date, time, ranges, extensionStart, timeZone));

export const getExtensionTimeOptionsForDate = (date, ranges, extensionStart, timeZone) => {
    if (!date?.isValid()) return timeOptions;

    return timeOptions.map((option) => ({
        ...option,
        disabled: isExtensionTimeBlocked(date, option.value, ranges, extensionStart, timeZone),
    }));
};

const isSameReservationBlock = (block, reservation) => {
    if (reservation?._id && block?._id && String(reservation._id) === String(block._id)) return true;

    const currPickup = reservation?.pickupDate;
    const currDropoff = reservation?.exactDropoffTime || reservation?.dropoffDate;
    if (!block?.pickupDate || !block?.dropoffDate || !currPickup || !currDropoff) return false;

    return dayjs(block.pickupDate).isSame(dayjs(currPickup)) && dayjs(block.dropoffDate).isSame(dayjs(currDropoff));
};

/**
 * Turns the `GET /api/car/:id/calender-blocks` payload into blocked ranges. Other reservations are
 * padded by the company's buffer time; the reservation being extended is left out.
 */
export const buildCalendarRangesFromPayload = (payload, { bufferMinutes = 0, excludeReservation } = {}) => {
    const reservations = Array.isArray(payload?.reservations) ? payload.reservations : [];
    const reservationRanges = reservations
        .filter((reservation) => !(excludeReservation && isSameReservationBlock(reservation, excludeReservation)))
        .map((reservation) => toCalendarRange(reservation?.pickupDate, reservation?.dropoffDate))
        .filter(Boolean)
        .map((range) =>
            bufferMinutes > 0
                ? {
                      start: range.start.subtract(bufferMinutes, "minute"),
                      end: range.end.add(bufferMinutes, "minute"),
                  }
                : range,
        );

    const carBlocks = Array.isArray(payload?.carBlocks) ? payload.carBlocks : [];
    const carBlockRanges = carBlocks
        .filter((block) => block?.isActive !== false)
        .map((block) => toCalendarRange(block?.startDate, block?.endDate))
        .filter(Boolean);

    return {
        reservationRanges,
        carBlockRanges,
        blockedTimeRanges: [...reservationRanges, ...carBlockRanges],
    };
};
